import { FC, useState, useCallback } from 'react';
import Button from 'v3/common/components/Button';
import { Modal } from 'react-bootstrap';
import classNames from 'classnames';

type ModalComponentNewProps = {
  isOpen: boolean;
  renderTitle: JSX.Element | string;
  renderBodyComponent: JSX.Element | string;

  size?: 'sm' | 'lg' | 'xl'; //use undefined for default size (md) "md" is not a valid value in current React-Bootstrap.
  className?: string;
  backdrop?: boolean | 'static';
  centered?: boolean;
  closeButton?: boolean;

  okBtnTitle: string;
  cancelBtnTitle: string;
  isOkBtnDisabled?: boolean;
  okBtnClassName?: string;
  cancelBtnClassName?: string;

  onClose?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  onSaveid?: string;

  shouldConfirmClose?: boolean | (() => boolean);
  confirmMessage?: string;
};

const ModalComponentNew: FC<ModalComponentNewProps> = (props) => {
  const {
    isOpen,
    renderTitle,
    renderBodyComponent,
    size = undefined,
    className = '',
    backdrop = true,
    centered = true,
    closeButton = true,
    onClose,
    onSave,
    onCancel,
    onSaveid = '',
    shouldConfirmClose = false,
    confirmMessage = 'Are you sure you want to close without saving?',
    okBtnTitle = 'Save',
    cancelBtnTitle = 'Cancel',
    isOkBtnDisabled = false,
    okBtnClassName = '',
    cancelBtnClassName = '',
  } = props;

  const [isConfirming, setIsConfirming] = useState(false);

  const combinedClassName = classNames('modal-component-new__wrapper', className, {
    open: isOpen,
  });

  const handleClose = useCallback(() => {
    const needsConfirm = typeof shouldConfirmClose === 'function' ? shouldConfirmClose() : shouldConfirmClose;
    if (needsConfirm) setIsConfirming(true);
    else onClose?.();
  }, [shouldConfirmClose, onClose]);

  const confirmClose = () => {
    setIsConfirming(false);
    onClose?.();
  };

  const cancelClose = () => setIsConfirming(false);

  const RenderCloseConfirmation = () => (
    <>
      <Modal.Header>
        <strong>Confirmation</strong>
      </Modal.Header>
      <Modal.Body>{confirmMessage}</Modal.Body>
      <Modal.Footer>
        <Button onClick={confirmClose}>Yes</Button>
        <Button onClick={cancelClose}>No</Button>
      </Modal.Footer>
    </>
  );

  const RenderFooterComponent = () => {
    if (!onSave || !onCancel) return null;
    const combinedOkBtnClassName = classNames('modal-component-new__footer__ok-btn', okBtnClassName);
    const combinedCancelBtnClassName = classNames(
      'modal-component-new__footer__cancel-btn',
      cancelBtnClassName
    );
    return (
      <>
        <Modal.Footer className="modal-component-new__footer">
          {onCancel && (
            <Button className={combinedCancelBtnClassName} onClick={onCancel}>
              <p className="btn-text">{cancelBtnTitle}</p>
            </Button>
          )}
          {onSave && (
            <Button
              className={combinedOkBtnClassName}
              onClick={onSave}
              id={onSaveid}
              disabled={isOkBtnDisabled}
            >
              <p>{okBtnTitle}</p>
            </Button>
          )}
        </Modal.Footer>
      </>
    );
  };

  if (!isOpen) return null;

  return (
    <Modal
      show={isOpen}
      onHide={handleClose}
      size={isConfirming ? undefined : size}
      backdrop={backdrop}
      centered={centered}
      className="modal-component-new"
    >
      <div className={combinedClassName}>
        {isConfirming ? (
          <RenderCloseConfirmation />
        ) : (
          <>
            {renderTitle && (
              <Modal.Header className="modal-component-new__header" closeButton={closeButton && !!onClose}>
                <Modal.Title className="modal-component-new__title">{renderTitle}</Modal.Title>
              </Modal.Header>
            )}
            {renderBodyComponent && (
              <Modal.Body className="modal-component-new__body">{renderBodyComponent}</Modal.Body>
            )}
            {<RenderFooterComponent />}
          </>
        )}
      </div>
    </Modal>
  );
};

export default ModalComponentNew;
